import { ListRenderItem } from '@shopify/flash-list';
import { Card } from 'components/Card';
import { Page } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { ExplorerSummaryRow } from 'screens/AssessmentScreen/Explorer/ExplorerSummaryRow';
import {
  selectAseCanChangeStatus,
  selectAseCannotEditReason,
  selectAseTplFieldSectionsForExplorer,
  selectAseTplHasFlags,
  selectAseTplHasSignatories,
  selectAseTplSectionIds,
} from 'store/assessments/selectors';
import { StatusChange } from '../StatusChange';
import { ExplorerAssetRow } from './ExplorerAssetRow';
import { ExplorerHeader } from './ExplorerHeader';
import { ExplorerQuestionsHeader } from './ExplorerQuestionsHeader';
import { ExplorerRepeatableRow } from './ExplorerRepeatableRow';
import { ExplorerSection } from './ExplorerSection';
import { ExplorerNavigation } from './types';
import { ExplorerFieldGroupRow } from './ExplorerFieldGroupRow';
import { MessageEdit } from '../MessageEdit';
import { ExplorerFlags } from './ExplorerFlags';
import { ExplorerSignatories } from './ExplorerSignatories';
import { List } from 'components/List';
import { GapSmall } from 'components/GapSmall';
import { GapMedium } from 'components/GapMedium';
import { View } from 'react-native';

interface Props {
  navigation: ExplorerNavigation;
}

type RenderItem =
  | {
      type: 'overview';
    }
  | {
      type: 'fields';
    }
  | {
      type: 'section';
      data: {
        id: string;
      };
    };

export const Explorer: React.FC<Props> = ({ navigation }) => {
  const fieldsList = useSelector(selectAseTplFieldSectionsForExplorer);
  const sectionIds = useSelector(selectAseTplSectionIds);
  const canChangeStatus = useSelector(selectAseCanChangeStatus);
  const cannotEditReason = useSelector(selectAseCannotEditReason);
  const hasSignatories = useSelector(selectAseTplHasSignatories);
  const hasFlags = useSelector(selectAseTplHasFlags);

  const renderItem: ListRenderItem<RenderItem> = React.useCallback(
    ({ item }) => {
      if (item.type === 'overview') {
        return (
          <>
            <GapSmall>
              {cannotEditReason && <MessageEdit />}
              <ExplorerSummaryRow navigation={navigation} />
              {canChangeStatus && <StatusChange />}
              {hasSignatories && <ExplorerSignatories />}
              {hasFlags && <ExplorerFlags />}
            </GapSmall>
            <SeparatorHorizontal
              style={{
                marginTop: theme.padding * 2,
                marginBottom: theme.padding,
              }}
            />
          </>
        );
      }
      if (item.type === 'fields') {
        return (
          <View style={{ marginTop: theme.padding }}>
            <GapMedium>
              <Card label="Address and Photo">
                <ExplorerAssetRow navigation={navigation} />
              </Card>
              {fieldsList.map((section) => (
                <Card key={section.id} label={section.name}>
                  {section.groups.map((group) =>
                    group.repeatable ? (
                      <ExplorerRepeatableRow
                        key={group.id}
                        groupId={group.id}
                        parent={null}
                      />
                    ) : (
                      <ExplorerFieldGroupRow
                        key={group.id}
                        groupId={group.id}
                      />
                    ),
                  )}
                </Card>
              ))}
            </GapMedium>
            <SeparatorHorizontal
              style={{
                marginTop: theme.padding * 2,
                marginBottom: theme.padding,
              }}
            />
          </View>
        );
      }
      if (item.type === 'section') {
        return <ExplorerSection key={item.data.id} id={item.data.id} />;
      }
      return null;
    },
    [
      canChangeStatus,
      cannotEditReason,
      fieldsList,
      hasFlags,
      hasSignatories,
      navigation,
    ],
  );

  return React.useMemo(
    () => (
      <Page
        header={<ExplorerHeader />}
        underHeader={<ExplorerQuestionsHeader />}
        additionalHeaderHeight={theme.padding + 44 + theme.padding}
      >
        <List
          paddingTop={0}
          getItemType={(item) => item.type}
          data={[
            { type: 'overview' },
            { type: 'fields' },
            ...sectionIds.map<RenderItem>((sectionId) => ({
              type: 'section',
              data: { id: sectionId },
            })),
          ]}
          keyExtractor={(item) =>
            `${item.type}-${item.type === 'section' ? `${item.data.id}` : ''}`
          }
          renderItem={renderItem}
        ></List>
      </Page>
    ),
    [renderItem, sectionIds],
  );
};
